import { L } from '@lib';
import { ApprovalServiceSnapshot, OrderApprovalHistory } from '@services';
import { Utils } from '@utils';
import { Divider, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import React from 'react';

const { Text } = Typography;

const toCamelCase = (obj: any): any => {
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k.charAt(0).toLowerCase() + k.slice(1), toCamelCase(v)])
    );
  }
  return obj;
};

const tagStyle = { margin: 0, minWidth: 100, textAlign: 'center' as const };

const getStatusTag = (isApproved?: boolean) => {
  if (isApproved === true) return <Tag color="green" style={tagStyle}>Approved</Tag>;
  if (isApproved === false) return <Tag color="red" style={tagStyle}>Declined</Tag>;
  return <Tag color="orange" style={tagStyle}>Pending</Tag>;
};

export const Columns = () => [
  {
    title: L('Status'),
    dataIndex: 'isApproved',
    key: 'isApproved',
    width: 100,
    render: (isApproved?: boolean) => getStatusTag(isApproved),
  },
  {
    title: L('Sent at'),
    dataIndex: 'creationTime',
    key: 'creationTime',
    width: 100,
    render: (date: Date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
  },
  {
    title: L('Responded at'),
    dataIndex: 'lastModificationTime',
    key: 'lastModificationTime',
    width: 100,
    render: (date?: Date | string, record?: OrderApprovalHistory) =>
      record?.isApproved != null && date && dayjs(date).format('DD/MM/YYYY HH:mm'),
  },
  {
    title: L('Responded by'),
    dataIndex: 'respondedByFullName',
    key: 'respondedByFullName',
    width: 120,
    render: (name?: string, record?: OrderApprovalHistory) =>
      record?.isApproved != null ? name || '—' : null,
  },
  {
    title: L('Response type'),
    dataIndex: 'approvalResponseType',
    key: 'approvalResponseType',
    width: 80,
    render: (type?: string, record?: OrderApprovalHistory) =>
      record?.isApproved != null && type ? <Tag>{type}</Tag> : null,
  },
];

const cellStyle: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: 13,
  borderBottom: '1px solid #f0f0f0',
};

const headerCellStyle: React.CSSProperties = {
  ...cellStyle,
  fontSize: 12,
  fontWeight: 600,
  color: '#8c8c8c',
  borderBottom: '2px solid #e8ecf1',
  textTransform: 'uppercase',
};

const partCellStyle: React.CSSProperties = {
  ...cellStyle,
  fontSize: 12,
  color: '#8c8c8c',
  backgroundColor: '#fafafa',
};

const summaryLineStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '4px 0',
};

export const ExpandedRowRender = (record: OrderApprovalHistory) => {
  if (!record.snapshot) return null;

  let services: ApprovalServiceSnapshot[];
  try {
    services = toCamelCase(JSON.parse(record.snapshot));
  } catch {
    return null;
  }

  if (!services?.length) return null;

  const labourPrice = services.reduce(
    (sum, s) =>
      sum +
      Utils.calculateLabourPrice({
        isFixed: s.isFixed,
        price: s.price || 0,
        discount: s.discount || 0,
        duration: s.duration || 1,
      }),
    0
  );

  const allExtras = services.flatMap((s) => s.extras ?? []);

  const partsPrice = allExtras.reduce((sum, e) => {
    const partTotal = (e.price || 0) * (e.quantity || 0);
    return sum + partTotal - (partTotal * (e.discount || 0)) / 100;
  }, 0);

  const subTotal = labourPrice + partsPrice;

  const serviceVat = services.reduce(
    (sum, s) =>
      sum +
      Utils.calculateVat({
        isFixed: s.isFixed,
        price: s.price || 0,
        discount: s.discount || 0,
        duration: s.duration || 1,
        vat: s.vat || 0,
      }),
    0
  );

  const partsVat = allExtras.reduce(
    (sum, e) =>
      sum +
      Utils.calculateVat({
        price: e.price || 0,
        discount: e.discount || 0,
        duration: e.quantity || 0,
        vat: e.vat || 0,
      }),
    0
  );

  const vatAmount = serviceVat + partsVat;
  const total = subTotal + vatAmount;

  return (
    <div style={{ padding: '8px 0' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <td style={headerCellStyle}>Name</td>
            <td style={{ ...headerCellStyle, width: 70 }}>Type</td>
            <td style={{ ...headerCellStyle, width: 90, textAlign: 'right' }}>Price</td>
            <td style={{ ...headerCellStyle, width: 70, textAlign: 'right' }}>Quantity</td>
            <td style={{ ...headerCellStyle, width: 70, textAlign: 'right' }}>Discount</td>
            <td style={{ ...headerCellStyle, width: 60, textAlign: 'right' }}>VAT</td>
            <td style={{ ...headerCellStyle, width: 90, textAlign: 'right' }}>Total</td>
          </tr>
        </thead>
        <tbody>
          {services.map((s, i) => {
            const svcTotal = Utils.calculateLabourPrice({
              isFixed: s.isFixed,
              price: s.price || 0,
              discount: s.discount || 0,
              duration: s.duration || 1,
            });
            return (
              <React.Fragment key={`svc-${i}`}>
                <tr>
                  <td style={{ ...cellStyle, fontWeight: 600 }}>{s.title}</td>
                  <td style={cellStyle}>{s.isFixed ? 'Fixed' : 'Labour'}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>{Utils.formatCurrency(s.price || 0)}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>{!s.isFixed ? `${s.duration || 0}h` : '0h'}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>{Utils.formatCurrency(s.discount || 0)}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>{Utils.formatCurrency(s.vat || 0)}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 600 }}>{Utils.formatCurrency(svcTotal)}</td>
                </tr>
                {s.extras?.map((e, j) => {
                  const partTotal =
                    (e.price || 0) * (e.quantity || 0) -
                    ((e.price || 0) * (e.quantity || 0) * (e.discount || 0)) / 100;
                  return (
                    <tr key={`ext-${j}`}>
                      <td style={{ ...partCellStyle, paddingLeft: 20 }}>{e.name}</td>
                      <td style={partCellStyle}>{e.type || 'Part'}</td>
                      <td style={{ ...partCellStyle, textAlign: 'right' }}>{Utils.formatCurrency(e.price || 0)}</td>
                      <td style={{ ...partCellStyle, textAlign: 'right' }}>{e.quantity || 0}</td>
                      <td style={{ ...partCellStyle, textAlign: 'right' }}>{Utils.formatCurrency(e.discount || 0)}</td>
                      <td style={{ ...partCellStyle, textAlign: 'right' }}>{Utils.formatCurrency(e.vat || 0)}</td>
                      <td style={{ ...partCellStyle, textAlign: 'right' }}>{Utils.formatCurrency(partTotal)}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      <Divider style={{ margin: '8px 0' }} />

      <div style={summaryLineStyle}>
        <Text>Labour</Text>
        <Text>{Utils.formatCurrency(labourPrice)}</Text>
      </div>
      <div style={summaryLineStyle}>
        <Text>Parts & Fluids</Text>
        <Text>{Utils.formatCurrency(partsPrice)}</Text>
      </div>
      <div style={summaryLineStyle}>
        <Text>VAT</Text>
        <Text>{Utils.formatCurrency(vatAmount)}</Text>
      </div>
      <Divider style={{ margin: '4px 0' }} />
      <div style={summaryLineStyle}>
        <Text strong>TOTAL</Text>
        <Text strong>{Utils.formatCurrency(total)}</Text>
      </div>
    </div>
  );
};
